import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Formik, Form, FastField } from 'formik'
import { Fieldset, Card, Paragraph, Heading } from '@vwfs-bronson/bronson-react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { CheckboxField } from '../../../components/CheckboxField'
import { useSelector } from 'react-redux'
import { getStorefrontData } from '../../../services/redux/features/storefront.redux'
import { VALIDATION_ERRORS } from '../PersonalDetails/validation-schema'
import { getOriginSystem } from '../../../services/common/utils'

interface Props {
  hasSubmited: boolean
  setVehicleConditionValues: (param) => void
  setIsDirty: (param: boolean) => void
}

const VehicleConditionAUDI = forwardRef(
  (
    {
      hasSubmited,
      setVehicleConditionValues,
      setIsDirty: setIsDirtyParent,
    }: Props,
    ref
  ) => {
    const { t, i18n } = useTranslation()
    const storefrontData = useSelector(getStorefrontData)
    const originSystem = useMemo(
      () => getOriginSystem(storefrontData),
      [storefrontData]
    )
    const [isDirty, setIsDirty] = useState(false)
    const formState = useRef(undefined as any)
    const submitFunctionRef = useRef<() => void>()

    useImperativeHandle(ref, () => ({
      submit: () => submitFunctionRef.current?.(),
      isValid: () => formState.current.isValid,
      validate: () => formState.current.isValid,
    }))

    const data = useMemo(
      () => ({
        companyName: storefrontData?.dealerData?.companyName,
        limitationPeriod: storefrontData?.dealerData?.limitationPeriod,
        approvalRequiredFeaturesUrl:
          storefrontData?.dealerData?.approvalRequiredFeaturesUrl,
        deviationsObjectiveRequirementsUrl:
          storefrontData?.dealerData?.deviationsObjectiveRequirementsUrl,
        notApprovalRequiredFeaturesUrl:
          storefrontData?.dealerData?.notApprovalRequiredFeaturesUrl,
        limitationPeriodUrl: storefrontData?.dealerData?.limitationPeriodUrl,
        salesTermsUrl: storefrontData?.dealerData?.salesTermsUrl,
        dataProtectionUrl: storefrontData?.dealerData?.dataProtectionUrl,
        cancellationPolicyUrl:
          storefrontData?.dealerData?.cancellationPolicyUrl,
      }),
      [storefrontData]
    )

    useEffect(() => {
      setIsDirtyParent(isDirty)
    }, [isDirty, setIsDirtyParent])

    useEffect(() => {
      if (hasSubmited) {
        setTouchedForm(formState.current)
      }
    }, [hasSubmited])

    const onChangeForm = async (e, formProps) => {
      const { name, type, checked, value } = e.target
      if (name) {
        const newValue = type === 'checkbox' ? checked : value
        await formProps.setFieldValue(name, newValue)
        await formProps.setFieldTouched(name)
        await setVehicleConditionValues({
          ...formProps.values,
          [name]: newValue,
        })
      }
    }

    const setTouchedForm = (formProps) => {
      Object.keys(formProps.values).forEach((field) => {
        formProps.setFieldTouched(field)
      })
    }

    const baseCheckboxValidation = Yup.boolean()
      .required(
        VALIDATION_ERRORS.valueRequired(
          i18n.t(`form:vehicleCondition:${originSystem}:title`)
        )
      )
      .oneOf(
        [true],
        VALIDATION_ERRORS.valueRequired(
          i18n.t(`form:vehicleCondition:${originSystem}:title`)
        )
      )

    const ValidationSchema = Yup.object().shape({
      ...(data?.approvalRequiredFeaturesUrl && {
        approvalRequiredFeatures: baseCheckboxValidation,
      }),
      ...(data?.deviationsObjectiveRequirementsUrl && {
        deviationsObjectiveRequirements: baseCheckboxValidation,
      }),
      ...(data?.limitationPeriodUrl && {
        limitationPeriod: baseCheckboxValidation,
      }),
      digitalTerms: baseCheckboxValidation,
      salesTerms: baseCheckboxValidation,
    })

    const buildText = (name: string, linkUrl: string = '') => (
      <>
        {i18n.exists(`form:vehicleCondition:${originSystem}:${name}:text1`) &&
          t(`form:vehicleCondition:${originSystem}:${name}:text1`, { ...data })}
        {i18n.exists(`form:vehicleCondition:${originSystem}:${name}:link`) && (
          <a href={linkUrl} target="_blank" rel="noreferrer">
            {t(`form:vehicleCondition:${originSystem}:${name}:link`)}
          </a>
        )}
        {i18n.exists(`form:vehicleCondition:${originSystem}:${name}:text2`) &&
          t(`form:vehicleCondition:${originSystem}:${name}:text2`, { ...data })}
      </>
    )

    return (
      <Formik
        innerRef={formState}
        initialValues={{
          approvalRequiredFeatures: false,
          deviationsObjectiveRequirements: false,
          limitationPeriod: false,
          digitalTerms: false,
          salesTerms: false,
        }}
        validationSchema={ValidationSchema}
        validateOnMount
        onSubmit={() => {}}
      >
        {(formProps) => {
          setIsDirty(formProps.dirty)
          submitFunctionRef.current = formProps.submitForm

          return (
            <Form
              id="vehicle-condition-form"
              onChange={(e) => onChangeForm(e, formProps)}
            >
              <Card
                title={
                  <Heading level={4} className="u-mb-none">
                    {t(`form:vehicleCondition:${originSystem}:title`, {
                      defaultValue: t('form:vehicleCondition:title'),
                    })}
                  </Heading>
                }
                className="u-text-left"
              >
                {(data?.approvalRequiredFeaturesUrl ||
                  data.deviationsObjectiveRequirementsUrl) && (
                  <>
                    <Paragraph className="u-mt">
                      <strong>
                        {t(`form:vehicleCondition:${originSystem}:subtitle1`, {
                          ...data,
                          defaultValue: '',
                        })}
                      </strong>
                    </Paragraph>
                    <Fieldset className="u-mt">
                      {data.approvalRequiredFeaturesUrl && (
                        <Fieldset.Row className="u-mb u-mt-none">
                          <FastField
                            name="approvalRequiredFeatures"
                            label={buildText(
                              'approvalRequiredFeatures',
                              data.approvalRequiredFeaturesUrl
                            )}
                            required
                            component={CheckboxField}
                          />
                        </Fieldset.Row>
                      )}
                      {data.deviationsObjectiveRequirementsUrl && (
                        <Fieldset.Row className="u-mb u-mt-none">
                          <FastField
                            name="deviationsObjectiveRequirements"
                            label={buildText(
                              'deviationsObjectiveRequirements',
                              data.deviationsObjectiveRequirementsUrl
                            )}
                            required
                            component={CheckboxField}
                          />
                        </Fieldset.Row>
                      )}
                    </Fieldset>
                  </>
                )}
                {data.notApprovalRequiredFeaturesUrl && (
                  <Paragraph>
                    {buildText('info1', data.notApprovalRequiredFeaturesUrl)}
                  </Paragraph>
                )}
                {data.limitationPeriodUrl && (
                  <>
                    <Paragraph className="u-mt">
                      <strong>
                        {t(`form:vehicleCondition:${originSystem}:subtitle2`, {
                          ...data,
                          defaultValue: '',
                        })}
                      </strong>
                    </Paragraph>
                    <Fieldset className="u-mt">
                      <Fieldset.Row className="u-mb u-mt-none">
                        <FastField
                          name="limitationPeriod"
                          label={buildText(
                            'limitationPeriod',
                            data.limitationPeriodUrl
                          )}
                          required
                          component={CheckboxField}
                        />
                      </Fieldset.Row>
                    </Fieldset>
                  </>
                )}
                <Paragraph>
                  <strong>
                    {t(`form:vehicleCondition:${originSystem}:subtitle3`, {
                      ...data,
                      defaultValue: '',
                    })}
                  </strong>
                </Paragraph>
                <Fieldset className="u-mt">
                  <Fieldset.Row className="u-mb u-mt-none">
                    <FastField
                      name="digitalTerms"
                      label={buildText('digitalTerms')}
                      required
                      component={CheckboxField}
                    />
                  </Fieldset.Row>
                  <Fieldset.Row className="u-mb u-mt-none">
                    <FastField
                      name="salesTerms"
                      label={buildText('salesTerms', data?.salesTermsUrl)}
                      required
                      component={CheckboxField}
                    />
                  </Fieldset.Row>
                  <Fieldset.Row className="u-mb u-mt-none">
                    <Paragraph>
                      {buildText('info2', data.dataProtectionUrl)}
                    </Paragraph>
                  </Fieldset.Row>
                  <Fieldset.Row className="u-mb u-mt-none">
                    <Paragraph>
                      {buildText('info3', data.cancellationPolicyUrl)}
                    </Paragraph>
                  </Fieldset.Row>
                </Fieldset>
              </Card>
            </Form>
          )
        }}
      </Formik>
    )
  }
)

export default VehicleConditionAUDI
